import React from 'react';
import { useTranslation } from 'react-i18next';
import { MessageSquare, Send, X } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { applyContentLocaleToUrl } from '../../utils/contentLocaleQuery';

const API_BASE = process.env.REACT_APP_API_BASE || 'http://localhost:3001';

export default function FeedbackFab() {
  const { t } = useTranslation();
  const { isAuthenticated, auth, authorizedFetch } = useAuth();
  const isCustomer = isAuthenticated && auth.type === 'CUSTOMER';

  const [open, setOpen] = React.useState(false);
  const [busy, setBusy] = React.useState(false);
  const [email, setEmail] = React.useState('');
  const [subject, setSubject] = React.useState('');
  const [message, setMessage] = React.useState('');
  const [error, setError] = React.useState<string | null>(null);
  const [success, setSuccess] = React.useState<string | null>(null);

  const reset = () => {
    setError(null);
    setSuccess(null);
    setSubject('');
    setMessage('');
    if (!isCustomer) setEmail('');
  };

  const closePanel = () => {
    setOpen(false);
    reset();
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (busy) return;
    setBusy(true);
    setError(null);
    setSuccess(null);
    try {
      const res = isCustomer
        ? await authorizedFetch(`${API_BASE}/customer/feedback`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              type: 'FEEDBACK',
              subject,
              message,
            }),
          })
        : await fetch(
            applyContentLocaleToUrl(`${API_BASE}/public/feedback`, navigator.language),
            {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              email,
              subject,
              message,
            }),
            },
          );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body as { message?: string }).message || t('public.fab_error_generic'));
      }

      setSuccess(t('public.fab_success'));
      setSubject('');
      setMessage('');
      if (!isCustomer) setEmail('');
    } catch (err) {
      setError(err instanceof Error ? err.message : t('public.fab_error_generic'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="fixed bottom-5 right-5 z-50 inline-flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-[#059669] to-[#ea580c] text-white shadow-xl transition hover:scale-105 hover:shadow-2xl focus:outline-none focus:ring-4 focus:ring-emerald-300/50"
        aria-label={t('public.fab_open_aria')}
      >
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full border border-emerald-400/40 opacity-60 motion-safe:animate-ping"
          style={{ animationDuration: '3.2s' }}
        />
        <span
          aria-hidden="true"
          className="pointer-events-none absolute inset-0 rounded-full border border-sky-300/30 opacity-50 motion-safe:animate-ping"
          style={{ animationDuration: '3.2s', animationDelay: '1.4s' }}
        />
        <MessageSquare className="h-6 w-6" strokeWidth={2.2} />
      </button>

      {open ? (
        <div className="fixed inset-0 z-[60] pointer-events-none">
          <button
            type="button"
            aria-label={t('public.fab_close_aria')}
            className="absolute inset-0 pointer-events-auto bg-slate-900/10 backdrop-blur-[2px]"
            onClick={closePanel}
          />

          <aside className="pointer-events-auto absolute bottom-24 right-5 w-[min(92vw,380px)] overflow-hidden rounded-2xl border border-slate-700/70 bg-gradient-to-br from-slate-900 via-slate-900 to-slate-800 shadow-2xl">
            <div className="flex items-center justify-between border-b border-slate-700/70 px-4 py-3">
              <div>
                <h3 className="text-base font-semibold text-white">{t('public.fab_title')}</h3>
                <p className="text-xs text-slate-300">{t('public.fab_subtitle')}</p>
              </div>
              <button
                type="button"
                onClick={closePanel}
                className="rounded-lg border border-slate-600 p-2 text-slate-200 hover:bg-slate-700/70"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <form onSubmit={submit} className="max-h-[68vh] space-y-3 overflow-y-auto px-4 py-3">
              {!isCustomer ? (
                <label className="block text-xs font-semibold text-slate-200">
                  {t('public.fab_email')}
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder={t('public.fab_placeholder_email')}
                    className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/50"
                  />
                </label>
              ) : null}

              <label className="block text-xs font-semibold text-slate-200">
                {t('public.fab_subject')}
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t('public.fab_placeholder_subject')}
                  className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/50"
                />
              </label>

              <label className="block text-xs font-semibold text-slate-200">
                {t('public.fab_message')}
                <textarea
                  rows={7}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  placeholder={t('public.fab_placeholder_message')}
                  className="mt-1 w-full rounded-lg border border-slate-600 bg-slate-800/80 px-3 py-2 text-sm text-slate-100 outline-none placeholder:text-slate-400 focus:border-brand-500 focus:ring-2 focus:ring-brand-400/50"
                />
              </label>

              {error ? (
                <p className="rounded-lg border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-700">
                  {error}
                </p>
              ) : null}
              {success ? (
                <p className="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-xs text-emerald-700">
                  {success}
                </p>
              ) : null}

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={closePanel}
                  className="rounded-lg border border-slate-600 bg-slate-800/80 px-4 py-2 text-xs font-semibold text-slate-200 hover:bg-slate-700/80"
                >
                  {t('public.fab_close')}
                </button>
                <button
                  type="submit"
                  disabled={busy}
                  className="inline-flex h-9 w-9 items-center justify-center rounded-lg bg-slate-900 text-white hover:bg-slate-800 disabled:opacity-60"
                  aria-label={busy ? t('public.fab_sending_aria') : t('public.fab_send_aria')}
                >
                  <Send className="h-4 w-4" />
                </button>
              </div>
            </form>
          </aside>
        </div>
      ) : null}
    </>
  );
}
